import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  X,
  CalendarDays,
  BookmarkCheck,
  Users,
  LifeBuoy,
  History,
  Cpu,
  ChevronLeft,
  Plug,
  Brain,
  Activity,
  LineChart,
  RefreshCw,
  BellRing,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ADMIN_SECTIONS } from "@/lib/areas/registry";
import { useAppSelector } from "@/store";
import { selectProfile } from "@/store/selectors";
import { useDock } from "@/components/command/DockContext";
import { useMyAccess } from "@/hooks/useMyAccess";

type Area = "ops" | "admin" | "dev";
type SectionKey = string;

interface SectionDef {
  key: SectionKey;
  label: string;
  icon: LucideIcon;
  href?: string;
  // admin section key (registry.ts) gating this dock entry; fail-open
  sectionKey?: string;
}

const OPS_PRIMARY: SectionDef[] = [
  { key: "calendar",            label: "Calendar",           icon: CalendarDays,  href: "/calendar" },
  { key: "bookings",            label: "Reservation",        icon: BookmarkCheck, href: "/bookings" },
  { key: "analytics",           label: "Analytics",          icon: LineChart,     href: "/analytics" },
  { key: "ticket",              label: "Smart AI Helper",    icon: LifeBuoy,      href: "/smart-helper" },
  { key: "agencies",            label: "Agency",             icon: Users,         href: "/agency" },
  { key: "supplier-connector",  label: "Supplier Connector", icon: Plug,          href: "/supplier-connector" },
  { key: "system-integration",  label: "System Integration", icon: Cpu,           href: "/system-integration" },
];

/**
 * I moduli admin per chiave, presi dal registro.
 *
 * La barra deve dire la STESSA cosa delle card: leggere l'elenco invece di
 * ripeterlo evita che una voce cambi stato in un posto solo.
 */
const ADMIN_MODULI = new Map(
  ADMIN_SECTIONS.flatMap((s) => s.modules).map((m) => [m.key, m]),
);

const ADMIN_PRIMARY_DOCK: SectionDef[] = [
  { key: "sync-center", label: "Sync Center", icon: RefreshCw, href: "/admin/sync-center", sectionKey: "sync" },
  { key: "alerts",      label: "Alerts",      icon: BellRing,  href: "/admin/alerts",      sectionKey: "alerts" },
];

const DEV_PRIMARY_DOCK: SectionDef[] = [
  { key: "auto-connector", label: "Auto Connector",       icon: Plug,     href: "/dev/auto-connector" },
  { key: "ai-mapper",      label: "AI Mapper",            icon: Brain,    href: "/dev/ai-mapper" },
  { key: "runtime",        label: "Runtime intelligence", icon: Activity, href: "/dev/runtime" },
];

const LANDING_PREF_KEYS: Record<string, string> = {
  "/":      "herika.dock.home.open",
  "/admin": "herika.dock.admin.open",
  "/dev":   "herika.dock.dev.open",
};

function getLandingPrefKey(pathname: string): string | null {
  return LANDING_PREF_KEYS[pathname] ?? null;
}

interface RightCommandBarProps {
  area?: Area;
}

/**
 * Fly-in dock dal bordo destro. La visibilità è governata da `DockContext`
 * così altri componenti (es. il placeholder di /dev) possono aprirla.
 *
 * Convenzione: in home (`/`) il dock parte aperto a meno che l'utente
 * non l'abbia chiuso esplicitamente in passato (preferenza persistita).
 * In tutte le altre rotte parte chiuso.
 */
export function RightCommandBar({ area = "ops" }: RightCommandBarProps) {
  const { open: dockOpen, setOpen: setDockOpen } = useDock();
  const [active, setActive] = React.useState<SectionKey | null>(null);
  const profile = useAppSelector(selectProfile);
  const { hasBookingEngine, canSection } = useMyAccess();
  const navigate = useNavigate();
  const location = useLocation();

  const landingPrefKey = getLandingPrefKey(location.pathname);

  // First mount per route: apply default open state.
  // Landing routes ("/", "/admin", "/dev") open by default with persisted
  // per-area preference. Sub-routes inherit whatever state was current.
  const lastPathRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (lastPathRef.current === location.pathname) return;
    lastPathRef.current = location.pathname;
    if (landingPrefKey) {
      const userPref = window.localStorage.getItem(landingPrefKey);
      setDockOpen(userPref !== "false");
    }
  }, [location.pathname, landingPrefKey, setDockOpen]);

  const items = React.useMemo<SectionDef[]>(() => {
    if (area === "admin")
      return ADMIN_PRIMARY_DOCK.filter(
        (s) => !s.sectionKey || canSection("admin", s.sectionKey),
      );
    if (area === "dev")   return DEV_PRIMARY_DOCK;
    return OPS_PRIMARY.filter(
      (s) => s.key !== "agencies" || profile.activeRole === "OTA",
    );
  }, [area, profile.activeRole, canSection]);

  /**
   * Le voci che non portano da nessuna parte.
   *
   * Due casi, e vengono da posti diversi:
   * - ops senza booking engine: tutto spento tranne System Integration,
   *   l'unica superficie utile senza il modulo;
   * - admin: le voci il cui modulo nel registro e' `comingSoon` o `nascosta`.
   *   La barra e' una SCORCIATOIA per le card della landing, quindi deve dire
   *   la stessa cosa: una card spenta li' e una voce cliccabile qui sono due
   *   risposte diverse alla stessa domanda, e l'operatore crede alla seconda.
   *   Si legge dal registro invece di ripetere l'elenco: una voce che cambia
   *   stato non deve doverlo cambiare in due posti.
   */
  const isItemDisabled = React.useCallback(
    (key: SectionKey) => {
      if (area === "ops") return !hasBookingEngine && key !== "system-integration";
      if (area === "admin") {
        const modulo = ADMIN_MODULI.get(key as string);
        return Boolean(modulo?.comingSoon || modulo?.nascosta);
      }
      return false;
    },
    [area, hasBookingEngine],
  );

  React.useEffect(() => {
    if (
      area === "ops" &&
      active === "agencies" &&
      profile.activeRole !== "OTA"
    ) {
      setActive(null);
    }
  }, [area, active, profile.activeRole]);

  React.useEffect(() => {
    setActive(null);
  }, [area]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (active) setActive(null);
      else if (dockOpen) setDockOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, dockOpen, setDockOpen]);

  const openHistory = () => {
    window.dispatchEvent(new Event("herika:open-history"));
  };

  const toggleDock = () => {
    if (dockOpen) {
      setActive(null);
      setDockOpen(false);
      if (landingPrefKey) window.localStorage.setItem(landingPrefKey, "false");
    } else {
      setDockOpen(true);
      if (landingPrefKey) window.localStorage.setItem(landingPrefKey, "true");
    }
  };

  const handleItemClick = (s: SectionDef) => {
    if (s.href) {
      navigate(s.href);
      setActive(null);
      setDockOpen(false);
      return;
    }
    setActive((cur) => (cur === s.key ? null : s.key));
  };

  const isDarkArea = area === "dev";

  return (
    <>
      {active && (
        <button
          type="button"
          aria-label="Chiudi pannello"
          onClick={() => setActive(null)}
          className="fixed inset-0 z-[7000] bg-foreground/10 backdrop-blur-[2px] animate-fadeIn"
        />
      )}

      {active && (
        <div className="fixed right-[88px] top-1/2 z-[7100] -translate-y-1/2">
          <aside
            className={cn(
              "flex h-[min(640px,80vh)] w-[min(92vw,400px)] flex-col overflow-hidden",
              "rounded-[20px] backdrop-blur-2xl",
              isDarkArea
                ? "bg-[#15161e]/92 ring-1 ring-white/[0.06]"
                : "bg-white/92",
              "shadow-[0_1px_0_rgba(255,255,255,0.7)_inset,0_24px_60px_-18px_rgba(15,15,15,0.20)]",
              "animate-panelIn origin-right",
            )}
          >
            {(() => {
              const cur = items.find((s) => s.key === active);
              if (!cur) return null;
              const Icon = cur.icon;
              return (
                <>
                  <header className="flex shrink-0 items-center justify-between gap-3 px-5 pt-4 pb-3.5">
                    <div className="flex items-center gap-2.5">
                      <Icon
                        className={cn(
                          "h-[16px] w-[16px]",
                          isDarkArea ? "text-white/70" : "text-foreground/70",
                        )}
                        strokeWidth={1.5}
                      />
                      <h2
                        className={cn(
                          "font-sans text-[14px] font-medium tracking-tight",
                          isDarkArea ? "text-white" : "text-foreground",
                        )}
                      >
                        {cur.label}
                      </h2>
                    </div>
                    <button
                      type="button"
                      onClick={() => setActive(null)}
                      className={cn(
                        "grid h-7 w-7 place-items-center rounded-full transition-colors",
                        isDarkArea
                          ? "text-white/40 hover:bg-white/[0.05] hover:text-white"
                          : "text-foreground/40 hover:bg-foreground/[0.05] hover:text-foreground",
                      )}
                      aria-label="Chiudi"
                    >
                      <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  </header>
                  <div
                    className={cn(
                      "mx-5 h-px",
                      isDarkArea
                        ? "bg-gradient-to-r from-transparent via-white/[0.08] to-transparent"
                        : "bg-gradient-to-r from-transparent via-foreground/[0.08] to-transparent",
                    )}
                  />
                  <div className="flex-1 overflow-y-auto" />

                </>
              );
            })()}
          </aside>
        </div>
      )}

      <div
        className="fixed right-0 top-1/2 z-[7200] flex -translate-y-1/2 items-center"
        aria-label="Comandi laterali"
        data-dock-trigger
      >
        <nav
          className={cn(
            "mr-2 flex w-[52px] flex-col items-center gap-0.5",
            "rounded-[18px] backdrop-blur-2xl px-1.5 py-2",
            isDarkArea
              ? "bg-white/[0.06] ring-1 ring-white/[0.06]"
              : "bg-white/75",
            "shadow-[0_1px_0_rgba(255,255,255,0.8)_inset,0_8px_24px_-10px_rgba(15,15,15,0.12)]",
            "transition-[transform,opacity] duration-300 ease-out",
            dockOpen
              ? "translate-x-0 opacity-100"
              : "pointer-events-none translate-x-[80px] opacity-0",
          )}
          aria-hidden={!dockOpen}
        >
          {items.map((s) => (
            <DockButton
              key={s.key}
              label={
                !isItemDisabled(s.key)
                  ? s.label
                  : area === "admin"
                    ? `${s.label} — in arrivo`
                    : `${s.label} — richiede Booking Engine`
              }
              icon={s.icon}
              isActive={active === s.key}
              onClick={() => {
                if (isItemDisabled(s.key)) return;
                handleItemClick(s);
              }}
              isDark={isDarkArea}
              isDisabled={isItemDisabled(s.key)}
            />
          ))}

          {area === "ops" && (
            <>
              <span
                aria-hidden
                className={cn(
                  "my-1 h-px w-5",
                  isDarkArea ? "bg-white/[0.08]" : "bg-foreground/[0.08]",
                )}
              />
              <DockButton
                label="Cronologia"
                icon={History}
                shortcut="⌘K"
                isActive={false}
                onClick={() => {
                  setActive(null);
                  openHistory();
                }}
                isDark={isDarkArea}
              />
            </>
          )}
        </nav>

        <button
          type="button"
          onClick={toggleDock}
          aria-label={
            dockOpen ? "Chiudi pannello laterale" : "Apri pannello laterale"
          }
          aria-expanded={dockOpen}
          className={cn(
            "grid h-14 w-5 place-items-center rounded-l-[10px] rounded-r-none",
            "backdrop-blur-2xl",
            isDarkArea
              ? "bg-white/[0.08] border-y border-l border-white/[0.06] hover:bg-white/[0.12]"
              : "bg-white/85 border-y border-l border-foreground/[0.05] hover:bg-white/95",
            "shadow-[-4px_0_12px_rgba(15,15,15,0.08)]",
            "transition-colors",
          )}
        >
          <ChevronLeft
            className={cn(
              "h-3.5 w-3.5 transition-transform duration-300",
              dockOpen && "rotate-180",
              isDarkArea ? "text-white/55" : "text-foreground/55",
            )}
            strokeWidth={2}
          />
        </button>
      </div>
    </>
  );
}

interface DockButtonProps {
  label: string;
  icon: LucideIcon;
  isActive: boolean;
  onClick: () => void;
  shortcut?: string;
  isDark?: boolean;
  isDisabled?: boolean;
}

function DockButton({
  label,
  icon: Icon,
  isActive,
  onClick,
  shortcut,
  isDark = false,
  isDisabled = false,
}: DockButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={isActive}
      aria-disabled={isDisabled}
      title={label}
      className={cn(
        "group relative grid h-9 w-9 place-items-center rounded-[10px]",
        "transition-[color,background-color] duration-150 ease-out",
        isDisabled && "opacity-35 cursor-not-allowed",
        isActive
          ? isDark
            ? "bg-white/[0.10] text-emerald-300"
            : "bg-foreground/[0.05] text-accent"
          : isDark
            ? "text-white/55 hover:bg-white/[0.06] hover:text-white"
            : "text-foreground/45 hover:bg-foreground/[0.03] hover:text-foreground",
      )}
    >
      <Icon
        className="h-[16px] w-[16px]"
        strokeWidth={isActive ? 1.7 : 1.5}
      />

      {isActive && (
        <span
          aria-hidden
          className={cn(
            "absolute -left-[6px] top-1/2 h-3.5 w-[2px] -translate-y-1/2 rounded-full animate-fadeIn",
            isDark ? "bg-emerald-300" : "bg-accent",
          )}
        />
      )}

      <span
        className={cn(
          "pointer-events-none absolute right-[44px] top-1/2 -translate-y-1/2 flex items-center gap-1.5 whitespace-nowrap rounded-md",
          "px-2 py-1 text-[10px] font-medium tracking-tight",
          isDark ? "bg-white text-[#0a0b14]" : "bg-foreground text-background",
          "opacity-0 shadow-[0_4px_12px_rgba(15,15,15,0.18)] transition-opacity duration-150 group-hover:opacity-100",
        )}
      >
        {label}
        {shortcut && (
          <span className={isDark ? "text-[#0a0b14]/55" : "text-background/55"}>
            {shortcut}
          </span>
        )}
      </span>
    </button>
  );
}
